import React, { useState, useEffect, useContext, useCallback, useRef } from 'react';
import { Card, Button, Select, Table, Tag, Space, Typography, Row, Col, message, List, Modal, Spin, Statistic, Grid, Tooltip, Alert, Result } from 'antd';
import { ClockCircleOutlined, LoginOutlined, LogoutOutlined, EnvironmentOutlined, HistoryOutlined, InfoCircleOutlined, CheckCircleOutlined, SyncOutlined } from '@ant-design/icons';
import { attendanceAPI } from '../../api';
import api from '../../api';
import { UserContext } from '../../context/userContext';
import MobileCard from '../../components/common/MobileCard';
import moment from 'moment';
import '../../styles/responsive-cards.css';
import './attendance.css';

const { Title, Text } = Typography;
const { Option } = Select;
const { useBreakpoint } = Grid;

const Attendance = () => {
    const screens = useBreakpoint();
    const { user } = useContext(UserContext);
    const [loading, setLoading] = useState(false);
    const [statusLoading, setStatusLoading] = useState(true);
    const [patients, setPatients] = useState([]);
    const [selectedPatient, setSelectedPatient] = useState(null);
    const [isClockedIn, setIsClockedIn] = useState(false);
    const [currentAttendance, setCurrentAttendance] = useState(null);
    const [marks, setMarks] = useState([]);
    const [history, setHistory] = useState([]);
    const [nextMarkInTime, setNextMarkInTime] = useState(null);
    const [countdown, setCountdown] = useState('');
    const notificationSent = useRef(false);

    const fetchStatus = useCallback(async (showManualLoading = false) => {
        if (showManualLoading) setStatusLoading(true);
        try {
            const response = await attendanceAPI.getStatus();
            if (response.data.isClockedIn) {
                setIsClockedIn(true);
                setCurrentAttendance(response.data.attendance);
                setMarks(response.data.marks || []);
                calculateNextMarkIn(response.data.attendance, response.data.marks || []);
            } else {
                setIsClockedIn(false);
                setCurrentAttendance(null);
                setMarks([]);
                setNextMarkInTime(null);
            }
        } catch (error) {
            console.error('Error fetching attendance status:', error);
            message.error('Failed to load current shift status');
        } finally {
            setStatusLoading(false);
        }
    }, []);

    const fetchPatients = useCallback(async () => {
        try {
            const response = await api.get('/patients', { params: { limit: 100 } });
            setPatients(response.data.patients || []);
        } catch (error) {
            console.error('Error fetching patients:', error);
        }
    }, []);

    const fetchHistory = useCallback(async () => {
        try {
            const response = await attendanceAPI.getHistory();
            setHistory(response.data.history || []);
        } catch (error) {
            console.error('Error fetching attendance history:', error);
        }
    }, []);

    useEffect(() => {
        if (user) {
            fetchStatus();
            fetchPatients();
            fetchHistory();
        }

        if ("Notification" in window && Notification.permission !== "granted") {
            Notification.requestPermission();
        }
    }, [user, fetchStatus, fetchPatients, fetchHistory]);

    const calculateNextMarkIn = (attendance, marks) => {
        const lastTimeStr = marks.length > 0 ? marks[0].timestamp : attendance.clock_in_time;
        const lastTime = moment(lastTimeStr);
        const next = lastTime.clone().add(2, 'minutes'); // Set to 2 mins for testing
        setNextMarkInTime(next);
        notificationSent.current = false;
    };

    useEffect(() => {
        if (!nextMarkInTime || !isClockedIn) {
            setCountdown('');
            return;
        }

        const interval = setInterval(() => {
            const now = moment();
            const diff = nextMarkInTime.diff(now);
            
            if (diff <= 0) {
                setCountdown('Due now!');
                if (!notificationSent.current) {
                    sendNotification();
                    notificationSent.current = true;
                }
            } else {
                const duration = moment.duration(diff);
                const mins = Math.floor(duration.asMinutes());
                const secs = duration.seconds();
                setCountdown(`${mins}m ${secs}s`);
            }
        }, 1000);

        return () => clearInterval(interval);
    }, [nextMarkInTime, isClockedIn]);

    const sendNotification = () => {
        if ("Notification" in window && Notification.permission === "granted") {
            try {
                new Notification("Attendance Reminder", {
                    body: "It's time to mark in for your shift!",
                    icon: "https://eirahealth.in/assets/images/EHS-logo.png"
                });
            } catch (e) {
                console.error("Browser notification failed:", e);
            }
        }
    };

    const handleClockIn = async () => {
        if (!selectedPatient) {
            message.warning('Please select a patient first');
            return;
        }

        setLoading(true);
        try {
            await attendanceAPI.clockIn({ patientId: selectedPatient });
            message.success('Shift started successfully');
            await fetchStatus(true);
            await fetchHistory();
        } catch (error) {
            message.error(error.response?.data?.message || 'Failed to clock in');
        } finally {
            setLoading(false);
        }
    };

    const getPosition = () => {
        return new Promise((resolve, reject) => {
            if (!navigator.geolocation) {
                reject(new Error("Geolocation not supported"));
                return;
            }
            navigator.geolocation.getCurrentPosition(resolve, reject, { 
                enableHighAccuracy: true,
                timeout: 15000,
                maximumAge: 0
            });
        });
    };

    const handleMarkIn = async () => {
        if (!isClockedIn) return;
        setLoading(true);
        const hide = message.loading('Verifying location...', 0);
        
        try {
            const position = await getPosition();
            const { latitude, longitude } = position.coords;
            
            await attendanceAPI.markIn({
                attendanceId: currentAttendance.id,
                latitude,
                longitude
            });
            
            message.success('Mark-in successful!');
            await fetchStatus();
        } catch (error) {
            if (error.response?.status === 400) {
                message.error(error.response.data.message);
            } else if (error.code) {
                message.error("Location access failed. Ensure GPS is ON and permission is granted.");
            } else {
                message.error('Failed to record mark-in.');
            }
        } finally {
            hide();
            setLoading(false);
        }
    };

    const handleClockOut = (attendanceId) => {
        const id = attendanceId || currentAttendance?.id;
        if (!id) return;

        Modal.confirm({
            title: 'End Current Shift?',
            content: 'Are you sure you want to end your shift?',
            okText: 'End Shift',
            okButtonProps: { danger: true },
            onOk: async () => {
                setLoading(true);
                try {
                    await attendanceAPI.clockOut({ attendanceId: id });
                    message.success('Shift ended successfully');
                    await fetchStatus(true);
                    await fetchHistory();
                } catch (error) {
                    message.error('Failed to end shift');
                } finally {
                    setLoading(false);
                }
            }
        });
    };

    const historyColumns = [
        {
            title: 'Date',
            dataIndex: 'date',
            key: 'date',
            render: (date) => moment(date).format('DD-MM-YYYY')
        },
        {
            title: 'Patient',
            dataIndex: 'patient_name',
            key: 'patient_name',
            render: (text, record) => `${text} (${record.unique_patient_id})`
        },
        {
            title: 'Clock In',
            dataIndex: 'clock_in_time',
            key: 'clock_in_time',
            render: (time) => moment(time).format('hh:mm A')
        },
        {
            title: 'Clock Out',
            dataIndex: 'clock_out_time',
            key: 'clock_out_time',
            render: (time, record) => time 
                ? moment(time).format('hh:mm A') 
                : <Button type="primary" size="small" danger onClick={() => handleClockOut(record.id)}>Clock Out</Button>
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            render: (status) => (
                <Tag color={status === 'Complete' ? 'green' : 'blue'}>{status}</Tag>
            )
        }
    ];

    if (statusLoading) {
        return <div style={{ textAlign: 'center', padding: '100px 0' }}><Spin size="large" tip="Verifying status..." /></div>;
    }

    const isMarkInDue = countdown === 'Due now!';

    return (
        <div className="attendance-container" style={{ padding: screens.xs ? '8px' : '16px' }}>
            <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Title level={4} style={{ margin: 0 }}><ClockCircleOutlined /> Shift & Attendance</Title>
                <Button icon={<SyncOutlined />} onClick={() => fetchStatus(true)}>Refresh</Button>
            </div>

            <Row gutter={[24, 24]}>
                <Col xs={24} lg={10}>
                    <Card 
                        className={isClockedIn ? "active-shift-card" : ""}
                        title={<span>Session Status</span>}
                        extra={isClockedIn ? <Tag color="green">ACTIVE</Tag> : <Tag>IDLE</Tag>}
                    >
                        {!isClockedIn ? (
                            <Space direction="vertical" style={{ width: '100%' }} size="large">
                                <div>
                                    <Text strong>Select Patient:</Text>
                                    {patients.length > 0 ? (
                                        <Select
                                            style={{ width: '100%', marginTop: '8px' }}
                                            placeholder="Choose patient"
                                            onChange={setSelectedPatient}
                                            value={selectedPatient}
                                            size="large"
                                        >
                                            {patients.map(p => (
                                                <Option key={p.id} value={p.id}>
                                                    {p.full_name} ({p.unique_patient_id})
                                                </Option>
                                            ))}
                                        </Select>
                                    ) : (
                                        <Alert message="No assigned patients found." type="info" showIcon style={{ marginTop: 8 }} />
                                    )}
                                </div>
                                <Button 
                                    type="primary" 
                                    icon={<LoginOutlined />} 
                                    size="large" 
                                    block 
                                    onClick={handleClockIn}
                                    loading={loading}
                                    disabled={!selectedPatient}
                                    style={{ height: '50px' }}
                                >
                                    CLOCK IN
                                </Button>
                            </Space>
                        ) : (
                            <Space direction="vertical" style={{ width: '100%' }} size="middle">
                                <div style={{ background: '#e6f7ff', padding: '16px', borderRadius: '12px' }}>
                                    <Statistic title="Current Patient" value={currentAttendance?.patient_name} />
                                    <div style={{ marginTop: '10px' }}>
                                        <Text type="secondary">Started at: </Text>
                                        <Text strong>{moment(currentAttendance?.clock_in_time).format('hh:mm A')}</Text>
                                    </div>
                                </div>

                                {isMarkInDue && (
                                    <Alert message="Mark-in Required" type="error" showIcon style={{ border: '2px solid #ff4d4f' }} />
                                )}

                                <Card 
                                    size="small" 
                                    type="inner" 
                                    title={<Space><EnvironmentOutlined /> Verification (2 min test interval)</Space>}
                                    className={isMarkInDue ? "mark-in-due-card" : ""}
                                >
                                    <Row align="middle" justify="space-between" gutter={[12, 12]}>
                                        <Col xs={24} sm={11}>
                                            <Statistic 
                                                title="Next Mark-in Due" 
                                                value={countdown} 
                                                valueStyle={{ color: isMarkInDue ? '#cf1322' : '#3f8600', fontWeight: 'bold' }} 
                                            />
                                        </Col>
                                        <Col xs={24} sm={13}>
                                            <Button 
                                                type="primary"
                                                danger={isMarkInDue}
                                                icon={<CheckCircleOutlined />} 
                                                onClick={handleMarkIn}
                                                loading={loading}
                                                block
                                                size="large"
                                                style={{ height: '50px' }}
                                            >
                                                MARK IN NOW
                                            </Button>
                                        </Col>
                                    </Row>
                                </Card>

                                <Button 
                                    danger 
                                    icon={<LogoutOutlined />} 
                                    size="large" 
                                    block 
                                    onClick={() => handleClockOut()}
                                    style={{ marginTop: '10px' }}
                                >
                                    CLOCK OUT
                                </Button>
                            </Space>
                        )}
                    </Card>
                </Col>

                <Col xs={24} lg={14}>
                    <Card title={<span><HistoryOutlined /> Shift Logs</span>} bodyStyle={{ padding: screens.xs ? '8px' : '16px' }}>
                        {screens.md ? (
                            <Table columns={historyColumns} dataSource={history} rowKey="id" pagination={{ pageSize: 8 }} size="small" />
                        ) : (
                            <div className="mobile-attendance-list">
                                {history.map(item => (
                                    <MobileCard
                                        key={item.id}
                                        title={moment(item.date).format('DD MMM YYYY')}
                                        tag={<Tag color={item.status === 'Complete' ? 'green' : 'blue'}>{item.status}</Tag>}
                                        fields={[
                                            { label: 'Patient', value: `${item.patient_name}` },
                                            { label: 'In', value: moment(item.clock_in_time).format('hh:mm A') },
                                            { label: 'Out', value: item.clock_out_time ? moment(item.clock_out_time).format('hh:mm A') : 'Active' }
                                        ]}
                                        actions={!item.clock_out_time ? [
                                            { label: 'Clock Out', onClick: () => handleClockOut(item.id), type: 'primary', danger: true, block: true }
                                        ] : []}
                                    />
                                ))}
                            </div>
                        )}
                    </Card>
                </Col>
            </Row>
        </div>
    );
};

export default Attendance;
